import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, resolveDynamicComponent as _resolveDynamicComponent, mergeProps as _mergeProps, withCtx as _withCtx, createBlock as _createBlock, withKeys as _withKeys } from "vue"

const _hoisted_1 = { class: "column gap-12 q-px-md q-mb-xl" }
const _hoisted_2 = { class: "fs-19px fw-600 text-cool-gray-100" }
const _hoisted_3 = { class: "fs-13px fw-500 text-cool-gray-80" }
const _hoisted_4 = {
  key: 0,
  class: "k-cropper__stage relative-position"
}
const _hoisted_5 = ["src"]
const _hoisted_6 = ["aspect-ratio"]
const _hoisted_7 = ["action"]
const _hoisted_8 = {
  key: 0,
  class: "k-cropper__divider self-stretch"
}
const _hoisted_9 = { class: "row no-wrap fs-15px fw-500 text-static-light" }
const _hoisted_10 = { class: "k-cropper__zoom-field" }
const _hoisted_11 = { "aria-hidden": "true" }
const _hoisted_12 = ["value", "aria-label"]
const _hoisted_13 = { class: "row no-wrap justify-center gap-12 q-mt-40" }

import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { debounce } from 'quasar'
import 'cropperjs'
import type CropperCanvas from '@cropper/element-canvas'
import type CropperImage from '@cropper/element-image'
import type CropperSelection from '@cropper/element-selection'

interface ITextObject {
  title: string
  description: string
  /** The close button's accessible name */
  close: string
  reset: string
  crop: string
  /** The zoom controls' accessible names */
  zoom: string
  zoomIn: string
  zoomOut: string
  flipHorizontal: string
  flipVertical: string
  rotate: string
  /** The ratio toggle's accessible names */
  portrait: string
  landscape: string
  square: string
}

interface IProps {
  image: File | null
  /** NaN for a free ratio, which the selection's corner handles can reshape */
  aspectRatio: number
  /**
   * Shows the ratio toggle, offering the aspect ratio in portrait and landscape, and 1:1. It starts at the aspect ratio
   * as given, which must be a fixed ratio other than 1:1 for the toggle to show.
   */
  ratioToggle?: boolean
  /**
   * Output size in px; the crop is scaled to fit inside it. Given for the aspect ratio as passed, and turned with the
   * ratio toggle to the other orientation.
   */
  width: number
  height: number
  /** A PNG keeps the image's transparency; a JPEG lays it on white */
  outputType?: 'image/jpeg' | 'image/png'
  /** JPEG only */
  quality?: number
  /** In bytes; a JPEG is compressed to fit it, a PNG (which has no quality setting) is scaled down instead */
  maxSize?: number | undefined
  text?: ITextObject
}

interface IEmits {
  (event: 'crop', data: File): void
}

/** What cropper-canvas reports with an `action` event; positions are in page coordinates */
interface ICanvasActionDetail {
  action: string
  relatedEvent?: Event
  /** A drag's last and current pointer positions */
  startX?: number
  startY?: number
  endX?: number
  endY?: number
  /** A wheel step, or a pinch's change in the fingers' distance, relative to the last event */
  scale?: number
  /** A pinch's midpoint */
  centerX?: number
  centerY?: number
}

interface IImageView {
  naturalWidth: number
  naturalHeight: number
  /** The image's centre in canvas coordinates, and its scale of the natural size */
  centerX: number
  centerY: number
  scale: number
  /** The scale at which the image just covers the selection: the furthest it can be zoomed out */
  minScale: number
  /**
   * The rotation and flips: where the image's own x and y axes point on screen, as [xAxisX, xAxisY, yAxisX, yAxisY]
   * (the transform matrix's a, b, c and d at a scale of 1), each 0, 1 or -1
   */
  orientation: [number, number, number, number]
}

/** A resize by a corner handle as it began: the selection's edges and the pointer's position */
interface ISelectionResize {
  left: number
  top: number
  right: number
  bottom: number
  pointerX: number
  pointerY: number
}

type RatioOption = 'portrait' | 'landscape' | 'square'

const SELECTION_COVERAGE = 0.8
// How far the image can be zoomed in, relative to the zoom at which it just covers the selection
const MAX_ZOOM = 10
// The zoom buttons step between multiples of this, in percent of the fit
const ZOOM_STEP = 10
// How long after the canvas stops resizing (e.g. with the window) the cropper is laid out again, in ms
const RESIZE_DEBOUNCE = 300
// The ratio toggle's options, in the order shown; each is also its text key
const CONTROLS_TINT = 'rgba(0, 0, 0, 0.25)'
// Narrower than this, in px, the three control groups don't fit on one row (e.g. on a phone), so the toolbar splits:
// only the zoom controls stay on the canvas, and the others move below it
const SPLIT_TOOLBAR_WIDTH = 410
// A free ratio's corner handles, by the cropperjs action each starts, and the smallest they resize the selection to
const MIN_SELECTION_SIZE = 40
const PNG = 'image/png'
const JPEG = 'image/jpeg'


export default /*@__PURE__*/_defineComponent({
  ...{ name: 'KCropper' },
  __name: 'KCropper',
  props: /*@__PURE__*/_mergeModels({
    image: {},
    aspectRatio: {},
    ratioToggle: { type: Boolean, default: false },
    width: {},
    height: {},
    outputType: { default: 'image/jpeg' },
    quality: { default: 1 },
    maxSize: { default: undefined },
    text: { default: () => ({
    title: 'Adjust Image',
    description: 'Resize and reposition your image to adjust how it will appear.',
    close: 'Close',
    reset: 'Reset',
    crop: 'Apply Image',
    zoom: 'Zoom level, in percent',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    flipHorizontal: 'Flip horizontally',
    flipVertical: 'Flip vertically',
    rotate: 'Rotate 90° clockwise',
    portrait: 'Portrait',
    landscape: 'Landscape',
    square: 'Square',
  }) }
  }, {
    "modelValue": { type: Boolean, ...{ required: true } },
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["crop"], ["update:modelValue"]),
  setup(__props: any, { emit: __emit }) {


const model = _useModel<boolean>(__props, "modelValue")
const props = __props
const emit = __emit

// The selection's share of the canvas along its constrained side; the rest shows the image around it
const RATIO_OPTIONS: { value: RatioOption; icon: string }[] = [
  { value: 'portrait', icon: 'portrait-2' },
  { value: 'landscape', icon: 'landscape-2' },
  { value: 'square', icon: 'square' },
]
// The controls' glass is tinted dark, under light icons and text, so they read over any image
const RESIZE_ACTIONS = ['nw-resize', 'ne-resize', 'sw-resize', 'se-resize']
const imagePreview = ref('')
const isImageFitted = ref(false)
/** The ratio toggle's choice, used only while it's shown */
const ratioOption = ref<RatioOption>(getDefaultRatioOption())
/** The zoom relative to the fit, which is 100% */
const zoomPercent = ref(100)
/** The zoom field's text: the zoom level, or an edit not applied yet */
const zoomInput = ref('100')
/** Whether the canvas is narrower than SPLIT_TOOLBAR_WIDTH */
const isToolbarSplit = ref(false)
const contentRef = ref<HTMLDivElement | null>(null)
const cropperCanvasRef = ref<CropperCanvas | null>(null)
const cropperImageRef = ref<CropperImage | null>(null)
const cropperSelectionRef = ref<CropperSelection | null>(null)

const isFreeRatio = computed(() => !(props.aspectRatio > 0))
// Only a fixed ratio other than 1:1 has an orientation to toggle
const isRatioToggleShown = computed(() => props.ratioToggle && props.aspectRatio > 0 && props.aspectRatio !== 1)
/** The selection's aspect ratio, NaN for a free one */
const selectionRatio = computed(() => {
  if (!isRatioToggleShown.value) return props.aspectRatio
  const landscape = Math.max(props.aspectRatio, 1 / props.aspectRatio)
  return { portrait: 1 / landscape, landscape, square: 1 }[ratioOption.value]
})
// The flip, rotate and ratio controls are glass over the canvas, but frosted below it, as in Figma, where glass would
// only have the popup's white behind it; their icons turn dark to suit
const outerControlsProps = computed(() =>
  isToolbarSplit.value ? { class: 'k-cropper__controls--frosted' } : { tint: CONTROLS_TINT },
)
const outerIconColor = computed(() => (isToolbarSplit.value ? 'cool-gray-80' : 'static-light'))

// Kept here rather than measured, so each drag or zoom is a little arithmetic and a single transform
const view: IImageView = {
  naturalWidth: 0,
  naturalHeight: 0,
  centerX: 0,
  centerY: 0,
  scale: 1,
  minScale: 1,
  orientation: [1, 0, 0, 1],
}
/** The canvas size the selection was last laid out for */
const layoutSize = { width: 0, height: 0 }
/** A free selection's ratio: the image's own, until the corner handles reshape it */
let freeRatio = 1
/** The resize by a corner handle under way, if any */
let resizeStart: ISelectionResize | null = null
// Lays the cropper out again once its canvas has stopped resizing for RESIZE_DEBOUNCE ms
const debouncedFollowCanvasResize = debounce(followCanvasResize, RESIZE_DEBOUNCE)
// Watches the canvas rather than the window, so only resizes that change the cropper's size count. The toolbar
// follows at once, before the resize is painted; a hidden canvas (no width) leaves it as it was.
const resizeObserver = new ResizeObserver(([entry]) => {
  const { width } = entry.contentRect
  if (width) isToolbarSplit.value = width < SPLIT_TOOLBAR_WIDTH
  debouncedFollowCanvasResize()
})

function setImagePreview(file: File | null) {
  if (imagePreview.value) URL.revokeObjectURL(imagePreview.value)
  imagePreview.value = file ? URL.createObjectURL(file) : ''
}

// A quarter turn swaps the image's sides on screen
function isQuarterTurned() {
  return view.orientation[0] === 0
}

function getImageSize(scale: number) {
  const { naturalWidth, naturalHeight } = view
  return isQuarterTurned()
    ? { width: naturalHeight * scale, height: naturalWidth * scale }
    : { width: naturalWidth * scale, height: naturalHeight * scale }
}

function getMinScale(selection: CropperSelection) {
  const { width, height } = getImageSize(1)
  return Math.max(selection.width / width, selection.height / height)
}

function getSelectionCenter(selection: CropperSelection) {
  return { x: selection.x + selection.width / 2, y: selection.y + selection.height / 2 }
}

// A free ratio (NaN) starts at the image's own, so the whole image can be cropped
function getAspectRatio() {
  return selectionRatio.value > 0 ? selectionRatio.value : freeRatio
}

function getCanvasSize(cropperCanvas: CropperCanvas) {
  const canvasStyle = getComputedStyle(cropperCanvas)
  return { width: parseFloat(canvasStyle.width), height: parseFloat(canvasStyle.height) }
}

// The ratio toggle's option for the aspect ratio as given
function getDefaultRatioOption(): RatioOption {
  return props.aspectRatio < 1 ? 'portrait' : 'landscape'
}

// Centres a selection of the aspect ratio in the canvas, covering SELECTION_COVERAGE of it along the constrained side.
// Works from the canvas's layout size instead of getBoundingClientRect, which is skewed while the popup's opening
// transition scales it (offsetWidth would round a fractional size).
function centerSelection(cropperCanvas: CropperCanvas, cropperSelection: CropperSelection) {
  const { width: canvasWidth, height: canvasHeight } = getCanvasSize(cropperCanvas)
  Object.assign(layoutSize, { width: canvasWidth, height: canvasHeight })
  const aspectRatio = getAspectRatio()
  const width = Math.min(canvasWidth, canvasHeight * aspectRatio) * SELECTION_COVERAGE
  const height = width / aspectRatio
  // cropperjs rounds these to whole pixels, so the image is fitted to the selection it ends up with. The ratio is
  // passed along, as the element's own attribute only follows on the next render.
  cropperSelection.$change((canvasWidth - width) / 2, (canvasHeight - height) / 2, width, height, selectionRatio.value)
}

// Places the image, clamped so that it always covers the selection
function setImageView(centerX: number, centerY: number, scale: number) {
  const cropperImage = cropperImageRef.value
  const cropperSelection = cropperSelectionRef.value
  if (!cropperImage || !cropperSelection) return

  const { width, height } = getImageSize(scale)
  const selection = cropperSelection
  view.centerX = clamp(centerX, selection.x + selection.width - width / 2, selection.x + width / 2)
  view.centerY = clamp(centerY, selection.y + selection.height - height / 2, selection.y + height / 2)
  view.scale = scale
  zoomPercent.value = Math.round((scale / view.minScale) * 100)

  const [xAxisX, xAxisY, yAxisX, yAxisY] = view.orientation
  // The image is laid out at its natural size in the canvas's top-left corner and transforms around its centre
  cropperImage.$setTransform(
    xAxisX * scale,
    xAxisY * scale,
    yAxisX * scale,
    yAxisY * scale,
    view.centerX - view.naturalWidth / 2,
    view.centerY - view.naturalHeight / 2,
  )
}

// Zooms around a point in canvas coordinates, keeping the part of the image under it in place
function zoomImage(factor: number, originX: number, originY: number) {
  if (!isImageFitted.value) return

  const scale = clamp(view.scale * factor, view.minScale, view.minScale * MAX_ZOOM)
  const ratio = scale / view.scale
  setImageView(originX + (view.centerX - originX) * ratio, originY + (view.centerY - originY) * ratio, scale)
}

function zoomAroundSelectionCentre(factor: number) {
  const cropperSelection = cropperSelectionRef.value
  if (!cropperSelection) return

  const center = getSelectionCenter(cropperSelection)
  zoomImage(factor, center.x, center.y)
}

function setZoomPercent(percent: number) {
  zoomAroundSelectionCentre((view.minScale * percent) / 100 / view.scale)
}

// Runs a change to the image's orientation or the selection's shape, then places the image at the same zoom level,
// with the part that was at the selection's centre back there. `mapOffset` maps the image centre's offset from that
// centre, for a change that turns or flips the image around it.
function keepView(change: () => void, mapOffset = (x: number, y: number): [number, number] => [x, y]) {
  const cropperSelection = cropperSelectionRef.value
  if (!cropperSelection || !isImageFitted.value) return

  // Capped, as a selection shrunk by its corner handles would take it past MAX_ZOOM once grown back
  const zoom = Math.min(view.scale / view.minScale, MAX_ZOOM)
  const before = getSelectionCenter(cropperSelection)
  const [offsetX, offsetY] = mapOffset(view.centerX - before.x, view.centerY - before.y)
  change()
  view.minScale = getMinScale(cropperSelection)

  const scale = view.minScale * zoom
  const ratio = scale / view.scale
  const center = getSelectionCenter(cropperSelection)
  setImageView(center.x + offsetX * ratio, center.y + offsetY * ratio, scale)
}

// Rotates or flips the image with cropperjs, keeping the part under the selection there
function reorientImage(
  transform: (cropperImage: CropperImage) => void,
  mapOffset: (x: number, y: number) => [number, number],
) {
  const cropperImage = cropperImageRef.value
  if (!cropperImage) return

  keepView(() => {
    transform(cropperImage)
    // cropperjs's quarter turn leaves float noise (cos 90° ≈ 6e-17), so the result is snapped back to whole steps
    const [xAxisX, xAxisY, yAxisX, yAxisY] = cropperImage.$getTransform().map((value) => Math.round(value / view.scale))
    view.orientation = [xAxisX, xAxisY, yAxisX, yAxisY]
  }, mapOffset)
}

// Reshapes the selection around its centre, keeping the image's rotation, flips and zoom level (so a fit stays a fit)
function selectRatio(option: RatioOption) {
  const cropperCanvas = cropperCanvasRef.value
  const cropperSelection = cropperSelectionRef.value
  if (!cropperCanvas || !cropperSelection || option === ratioOption.value) return

  keepView(() => {
    ratioOption.value = option
    centerSelection(cropperCanvas, cropperSelection)
  })
}

function rotateClockwise() {
  // cropperjs composes each step with the image's current orientation, so a mirrored image (one whose axes have
  // swapped handedness: a negative determinant) turns the other way
  const [xAxisX, xAxisY, yAxisX, yAxisY] = view.orientation
  const isMirrored = xAxisX * yAxisY - xAxisY * yAxisX < 0
  reorientImage(
    (cropperImage) => cropperImage.$rotate(isMirrored ? '-90deg' : '90deg'),
    (x, y) => [-y, x],
  )
}

// After a quarter turn, the image's own axes, which cropperjs scales, run the other way on screen
function flipHorizontal() {
  reorientImage(
    (cropperImage) => (isQuarterTurned() ? cropperImage.$scale(1, -1) : cropperImage.$scale(-1, 1)),
    (x, y) => [-x, y],
  )
}

function flipVertical() {
  reorientImage(
    (cropperImage) => (isQuarterTurned() ? cropperImage.$scale(-1, 1) : cropperImage.$scale(1, -1)),
    (x, y) => [x, -y],
  )
}

// cropperjs reports each drag, wheel step and pinch as an action on the canvas, before the image and selection act
// on it. Handling it here instead keeps the selection fixed and moves the image under it.
function onCanvasAction(event: CustomEvent<ICanvasActionDetail>) {
  event.preventDefault()
  const cropperCanvas = cropperCanvasRef.value
  if (!cropperCanvas || !isImageFitted.value) return

  const { action, relatedEvent, startX = 0, startY = 0, endX = 0, endY = 0, scale } = event.detail
  if (action === 'move') {
    setImageView(view.centerX + endX - startX, view.centerY + endY - startY, view.scale)
  } else if (RESIZE_ACTIONS.includes(action)) {
    resizeSelection(action, startX, startY, endX, endY)
  } else if ((action === 'scale' || action === 'transform') && scale) {
    const canvasRect = cropperCanvas.getBoundingClientRect()
    if (relatedEvent instanceof WheelEvent) {
      // As in cropperjs, a step out undoes a step in
      const factor = scale < 0 ? 1 / (1 - scale) : 1 + scale
      zoomImage(factor, relatedEvent.clientX - canvasRect.left, relatedEvent.clientY - canvasRect.top)
    } else {
      // A pinch zooms by the change in the fingers' distance, around their midpoint; any rotation is ignored
      const { centerX = 0, centerY = 0 } = event.detail
      zoomImage(1 + scale, centerX - scrollX - canvasRect.left, centerY - scrollY - canvasRect.top)
    }
  }
}

// Resizes the selection by a corner handle, following the pointer from where the resize began, within the image and
// the canvas and down to MIN_SELECTION_SIZE. The image stays put until the handle is let go.
function resizeSelection(action: string, startX: number, startY: number, endX: number, endY: number) {
  const cropperSelection = cropperSelectionRef.value
  if (!cropperSelection) return

  // The first move of a resize starts where the pointer went down
  if (!resizeStart) {
    const { x, y, width, height } = cropperSelection
    resizeStart = { left: x, top: y, right: x + width, bottom: y + height, pointerX: startX, pointerY: startY }
  }
  const { width, height } = getImageSize(view.scale)
  const minX = Math.max(view.centerX - width / 2, 0)
  const maxX = Math.min(view.centerX + width / 2, layoutSize.width)
  const minY = Math.max(view.centerY - height / 2, 0)
  const maxY = Math.min(view.centerY + height / 2, layoutSize.height)
  const offsetX = endX - resizeStart.pointerX
  const offsetY = endY - resizeStart.pointerY
  let { left, top, right, bottom } = resizeStart
  // The handle's corner, e.g. 'nw' for the top-left one
  const [corner] = action.split('-')
  if (corner.includes('w')) left = clamp(left + offsetX, minX, right - MIN_SELECTION_SIZE)
  if (corner.includes('e')) right = clamp(right + offsetX, left + MIN_SELECTION_SIZE, maxX)
  if (corner.includes('n')) top = clamp(top + offsetY, minY, bottom - MIN_SELECTION_SIZE)
  if (corner.includes('s')) bottom = clamp(bottom + offsetY, top + MIN_SELECTION_SIZE, maxY)
  cropperSelection.$change(left, top, right - left, bottom - top)
}

// Once a corner handle is let go, grows the selection back to the centre at its new ratio and zooms the image with
// it, so what was framed stays framed
function onCanvasActionEnd() {
  const cropperCanvas = cropperCanvasRef.value
  const cropperSelection = cropperSelectionRef.value
  if (!resizeStart || !cropperCanvas || !cropperSelection) return

  resizeStart = null
  freeRatio = cropperSelection.width / cropperSelection.height
  // The zoom carries over relative to the resized selection
  view.minScale = getMinScale(cropperSelection)
  keepView(() => centerSelection(cropperCanvas, cropperSelection))
}

// Centres a fixed selection of the aspect ratio as given in the canvas and zooms the image out until it just covers
// it, with no rotation or flip.
// Works from layout sizes (computed size / natural size) instead of getBoundingClientRect, which is skewed while
// the popup's opening transition scales it, so the fit is correct from the first frame.
async function fitImageAndSelection() {
  const cropperCanvas = cropperCanvasRef.value
  const cropperImage = cropperImageRef.value
  const cropperSelection = cropperSelectionRef.value
  if (!cropperCanvas || !cropperImage || !cropperSelection) return

  const src = imagePreview.value
  let image: HTMLImageElement
  try {
    image = await cropperImage.$ready()
  } catch {
    return
  }
  // The popup was closed or the image replaced while it was loading
  if (cropperImageRef.value !== cropperImage || imagePreview.value !== src) return

  const { naturalWidth, naturalHeight } = image
  Object.assign(view, { naturalWidth, naturalHeight, orientation: [1, 0, 0, 1] })
  freeRatio = naturalWidth / naturalHeight
  ratioOption.value = getDefaultRatioOption()
  centerSelection(cropperCanvas, cropperSelection)

  view.minScale = getMinScale(cropperSelection)
  const center = getSelectionCenter(cropperSelection)
  setImageView(center.x, center.y, view.minScale)
  isImageFitted.value = true
}

// Lays the selection out again for the canvas's new size and scales the image with it, keeping the crop, rotation,
// flips, ratio and zoom level as they were
function followCanvasResize() {
  const cropperCanvas = cropperCanvasRef.value
  const cropperSelection = cropperSelectionRef.value
  if (!cropperCanvas || !cropperSelection) return

  const { width, height } = getCanvasSize(cropperCanvas)
  // Unchanged, hidden (no size to lay out for), or mid-resize (laid out once the handle is let go)
  if ((width === layoutSize.width && height === layoutSize.height) || !width || !height || resizeStart) return
  keepView(() => centerSelection(cropperCanvas, cropperSelection))
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

// The buttons step to the next multiple of ZOOM_STEP, so from 133% in goes to 140% and out to 130%. The small
// tolerance keeps a zoom a float's breadth off a multiple (e.g. 109.9999…%) from counting as below it.
function zoomIn() {
  const steps = (view.scale / view.minScale) * (100 / ZOOM_STEP)
  setZoomPercent((Math.floor(steps + 1e-9) + 1) * ZOOM_STEP)
}

function zoomOut() {
  const steps = (view.scale / view.minScale) * (100 / ZOOM_STEP)
  setZoomPercent((Math.ceil(steps - 1e-9) - 1) * ZOOM_STEP)
}

function onZoomInput(event: Event) {
  const input = event.target as HTMLInputElement
  // Digits only, as the % sign sits outside the field; the element is reset too, as typing a rejected character
  // leaves the value unchanged and so wouldn't re-render it
  input.value = zoomInput.value = input.value.replace(/\D/g, '')
}

// Selected whole, so typing replaces it
function onZoomFocus(event: FocusEvent) {
  ;(event.target as HTMLInputElement).select()
}

// Applied through the change event that leaving the field fires
function onZoomEnter(event: KeyboardEvent) {
  ;(event.target as HTMLInputElement).blur()
}

// Out-of-range levels are clamped by the zoom; an empty field just shows the current level again
function applyZoomInput() {
  const percent = Number(zoomInput.value)
  if (percent) setZoomPercent(percent)
  zoomInput.value = String(zoomPercent.value)
}

async function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error('Blob conversion failed'))
        resolve(blob)
      },
      type,
      quality,
    )
  })
}

async function compressToMaxSize(canvas: HTMLCanvasElement, maxBytes: number): Promise<Blob> {
  let minQuality = 0.4
  let maxQuality = 1
  let bestBlob: Blob | null = null

  for (let i = 0; i < 7; i++) {
    const quality = (minQuality + maxQuality) / 2

    const blob = await canvasToBlob(canvas, JPEG, quality)

    if (blob.size > maxBytes) {
      // too big → lower quality
      maxQuality = quality
    } else {
      // good → try higher quality
      minQuality = quality
      bestBlob = blob
    }
  }

  // fallback if nothing matched
  if (!bestBlob) bestBlob = await canvasToBlob(canvas, JPEG, minQuality)

  return bestBlob
}

// The output size props, turned when the ratio toggle has turned the aspect ratio to the other orientation
function getOutputBox() {
  const option = ratioOption.value
  const isTurned = isRatioToggleShown.value && option !== 'square' && option !== getDefaultRatioOption()
  return isTurned ? { width: props.height, height: props.width } : { width: props.width, height: props.height }
}

// The output size in px for the aspect ratio, fitted inside a maximum: exactly the maximum when it has that ratio
function getOutputSize(maxWidth: number, maxHeight: number) {
  const aspectRatio = getAspectRatio()
  const width = Math.max(Math.min(maxWidth, Math.round(maxHeight * aspectRatio)), 1)
  return { width, height: Math.max(Math.round(width / aspectRatio), 1) }
}

// Draws the crop at exactly width × height px, which cropperjs's $toCanvas can't: it sizes the output from the
// selection, whose whole-pixel size rarely has the exact ratio. What's drawn is the region of the exact ratio
// centred in the selection (less than a pixel off it), through the same transform cropper-image applies.
async function drawCrop(width: number, height: number, background?: string) {
  const cropperImage = cropperImageRef.value
  const selection = cropperSelectionRef.value
  if (!cropperImage || !selection) throw new Error('The cropper is not open')

  const image = await cropperImage.$ready()
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Canvas 2D context unavailable')

  if (background) {
    context.fillStyle = background
    context.fillRect(0, 0, width, height)
  }
  // Output px per canvas px, at which the region just fits inside the selection
  const scale = Math.max(width / selection.width, height / selection.height)
  const regionX = selection.x + (selection.width - width / scale) / 2
  const regionY = selection.y + (selection.height - height / scale) / 2
  const [xAxisX, xAxisY, yAxisX, yAxisY] = view.orientation
  context.imageSmoothingQuality = 'high'
  context.setTransform(scale, 0, 0, scale, -regionX * scale, -regionY * scale)
  context.transform(
    xAxisX * view.scale,
    xAxisY * view.scale,
    yAxisX * view.scale,
    yAxisY * view.scale,
    view.centerX,
    view.centerY,
  )
  context.drawImage(image, -view.naturalWidth / 2, -view.naturalHeight / 2)
  return canvas
}

async function cropToJpeg(): Promise<Blob> {
  const box = getOutputBox()
  const { width, height } = getOutputSize(box.width, box.height)
  // JPEG has no transparency, so transparent pixels (e.g. of a PNG) are laid on white instead of turning black
  const canvas = await drawCrop(width, height, '#fff')
  return props.maxSize ? compressToMaxSize(canvas, props.maxSize) : canvasToBlob(canvas, JPEG, props.quality)
}

// PNG ignores quality, so it's fitted to maxSize by re-cropping at smaller dimensions. The file size follows the
// pixel count, so each pass scales both sides by the square root of the overshoot (with a little margin).
async function cropToPng(): Promise<Blob> {
  const box = getOutputBox()
  let { width, height } = getOutputSize(box.width, box.height)
  let blob = await canvasToBlob(await drawCrop(width, height), PNG)

  for (let i = 0; props.maxSize && blob.size > props.maxSize && i < 5; i++) {
    const scale = Math.sqrt(props.maxSize / blob.size) * 0.95
    ;({ width, height } = getOutputSize(Math.floor(width * scale), Math.floor(height * scale)))
    blob = await canvasToBlob(await drawCrop(width, height), PNG)
  }
  return blob
}

async function crop() {
  if (!isImageFitted.value) return

  const isPng = props.outputType === PNG
  const blob = isPng ? await cropToPng() : await cropToJpeg()
  emit('crop', new File([blob], isPng ? 'image.png' : 'image.jpg', { type: blob.type }))
  model.value = false
}

// Runs when the popup content mounts (the ref is set) and when the image is replaced while it is open.
// The image gets a fresh object URL each time: for a URL the browser has already decoded, `$ready()`
// resolves before cropperjs's own load handling runs, and that handling would then override the fit.
watch(
  [contentRef, () => props.image],
  async ([content]) => {
    isImageFitted.value = false
    setImagePreview(content ? props.image : null)
    if (!content) return

    // Lets the canvas mount with the new source
    await nextTick()
    fitImageAndSelection()
  },
  { flush: 'post' },
)

// Shows the zoom level as it changes, replacing any edit not applied yet
watch(zoomPercent, (percent) => (zoomInput.value = String(percent)))

// Observes each canvas as it mounts, dropping any layout still pending when it goes
watch(cropperCanvasRef, (cropperCanvas, oldCropperCanvas) => {
  if (oldCropperCanvas) resizeObserver.unobserve(oldCropperCanvas)
  if (cropperCanvas) resizeObserver.observe(cropperCanvas)
  else debouncedFollowCanvasResize.cancel()
})

onBeforeUnmount(() => {
  resizeObserver.disconnect()
  debouncedFollowCanvasResize.cancel()
  setImagePreview(null)
})

return (_ctx: any,_cache: any) => {
  const _component_KBtn = _resolveComponent("KBtn")!
  const _component_KLiquidGlass = _resolveComponent("KLiquidGlass")!
  const _component_KPopup = _resolveComponent("KPopup")!

  return (_openBlock(), _createBlock(_component_KPopup, {
    modelValue: model.value,
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((model).value = $event)),
    "max-width": "500px",
    class: "k-cropper",
    "content-class": "k-cropper__content relative-position bg-white"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_KBtn, {
        model: "link",
        size: "md",
        icon: "close-rounded",
        "custom-color": "cool-gray-70",
        padding: "4px",
        class: "absolute-top-right q-ma-12",
        "aria-label": __props.text.close,
        onClick: _cache[0] || (_cache[0] = ($event: any) => (model.value = false))
      }, null, 8, ["aria-label"]),
      _createElementVNode("div", {
        ref_key: "contentRef",
        ref: contentRef,
        class: "column no-wrap"
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("span", _hoisted_2, _toDisplayString(__props.text.title), 1),
          _createElementVNode("span", _hoisted_3, _toDisplayString(__props.text.description), 1)
        ]),
        (imagePreview.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("cropper-canvas", {
                ref_key: "cropperCanvasRef",
                ref: cropperCanvasRef,
                background: "",
                "theme-color": "color-mix(in srgb, var(--space-b-100) 50%, transparent)",
                class: _normalizeClass(["k-cropper__canvas rounded-16", { 'k-cropper__canvas--pending': !isImageFitted.value }]),
                onAction: onCanvasAction,
                onActionend: onCanvasActionEnd
              }, [
                _createElementVNode("cropper-image", {
                  ref_key: "cropperImageRef",
                  ref: cropperImageRef,
                  src: imagePreview.value,
                  alt: "Picture",
                  rotatable: "",
                  scalable: "",
                  translatable: "",
                  "initial-fit": "contain"
                }, null, 8, _hoisted_5),
                _cache[5] || (_cache[5] = _createElementVNode("cropper-shade", { hidden: "" }, null, -1)),
                _cache[6] || (_cache[6] = _createElementVNode("cropper-handle", {
                  action: "move",
                  plain: ""
                }, null, -1)),
                _createElementVNode("cropper-selection", {
                  ref_key: "cropperSelectionRef",
                  ref: cropperSelectionRef,
                  outlined: "",
                  "aspect-ratio": selectionRatio.value
                }, [
                  _cache[2] || (_cache[2] = _createElementVNode("cropper-grid", {
                    role: "grid",
                    covered: "",
                    "theme-color": "color-mix(in srgb, var(--cool-gray-50) 50%, transparent)"
                  }, null, -1)),
                  _cache[3] || (_cache[3] = _createElementVNode("cropper-crosshair", { centered: "" }, null, -1)),
                  _cache[4] || (_cache[4] = _createElementVNode("cropper-handle", {
                    action: "move",
                    "theme-color": "rgba(255, 255, 255, 0.0)"
                  }, null, -1)),
                  (isFreeRatio.value)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, _renderList(RESIZE_ACTIONS, (action) => {
                        return _createElementVNode("cropper-handle", {
                          key: action,
                          action: action,
                          "theme-color": "var(--space-b-100)"
                        }, null, 8, _hoisted_7)
                      }), 64))
                    : _createCommentVNode("", true)
                ], 8, _hoisted_6)
              ], 34),
              _createElementVNode("div", {
                class: _normalizeClass(["k-cropper__toolbar row justify-between gap-sm no-pointer-events", { 'k-cropper__toolbar--split': isToolbarSplit.value }])
              }, [
                (_openBlock(), _createBlock(_resolveDynamicComponent(isToolbarSplit.value ? 'div' : 'KLiquidGlass'), _mergeProps(outerControlsProps.value, { class: "k-cropper__controls row no-wrap items-center justify-center gap-sm rounded-12 all-pointer-events" }), {
                  default: _withCtx(() => [
                    _createVNode(_component_KBtn, {
                      model: "link",
                      size: "md",
                      icon: "flip-horizontal",
                      "custom-color": outerIconColor.value,
                      "aria-label": __props.text.flipHorizontal,
                      onClick: flipHorizontal
                    }, null, 8, ["custom-color", "aria-label"]),
                    _cache[7] || (_cache[7] = _createElementVNode("div", { class: "k-cropper__divider self-stretch" }, null, -1)),
                    _createVNode(_component_KBtn, {
                      model: "link",
                      size: "md",
                      icon: "flip-vertical",
                      "custom-color": outerIconColor.value,
                      "aria-label": __props.text.flipVertical,
                      onClick: flipVertical
                    }, null, 8, ["custom-color", "aria-label"]),
                    _cache[8] || (_cache[8] = _createElementVNode("div", { class: "k-cropper__divider self-stretch" }, null, -1)),
                    _createVNode(_component_KBtn, {
                      model: "link",
                      size: "md",
                      icon: "rotate-2",
                      "custom-color": outerIconColor.value,
                      "aria-label": __props.text.rotate,
                      onClick: rotateClockwise
                    }, null, 8, ["custom-color", "aria-label"])
                  ]),
                  _: 1
                }, 16)),
                (isRatioToggleShown.value)
                  ? (_openBlock(), _createBlock(_resolveDynamicComponent(isToolbarSplit.value ? 'div' : 'KLiquidGlass'), _mergeProps({ key: 0 }, outerControlsProps.value, { class: "k-cropper__controls row no-wrap items-center justify-center gap-sm rounded-12 all-pointer-events" }), {
                      default: _withCtx(() => [
                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(RATIO_OPTIONS, (option, index) => {
                          return (_openBlock(), _createElementBlock(_Fragment, {
                            key: option.value
                          }, [
                            index
                              ? (_openBlock(), _createElementBlock("div", _hoisted_8))
                              : _createCommentVNode("", true),
                            _createVNode(_component_KBtn, {
                              model: "link",
                              size: "md",
                              icon: option.icon,
                              "custom-color": ratioOption.value === option.value ? 'space-b-100' : outerIconColor.value,
                              "aria-label": __props.text[option.value],
                              "aria-pressed": ratioOption.value === option.value,
                              onClick: ($event: any) => (selectRatio(option.value))
                            }, null, 8, ["icon", "custom-color", "aria-label", "aria-pressed", "onClick"])
                          ], 64))
                        }), 64))
                      ]),
                      _: 1
                    }, 16))
                  : _createCommentVNode("", true),
                _createVNode(_component_KLiquidGlass, {
                  class: "k-cropper__controls row no-wrap items-center justify-center gap-sm rounded-12 all-pointer-events",
                  tint: CONTROLS_TINT
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_KBtn, {
                      model: "link",
                      size: "md",
                      icon: "zoom-out",
                      "custom-color": "static-light",
                      "aria-label": __props.text.zoomOut,
                      onClick: zoomOut
                    }, null, 8, ["aria-label"]),
                    _createElementVNode("div", _hoisted_9, [
                      _createElementVNode("label", _hoisted_10, [
                        _createElementVNode("span", _hoisted_11, _toDisplayString(zoomInput.value || ' '), 1),
                        _createElementVNode("input", {
                          value: zoomInput.value,
                          type: "text",
                          inputmode: "numeric",
                          size: "1",
                          maxlength: "4",
                          class: "text-right",
                          "aria-label": __props.text.zoom,
                          onInput: onZoomInput,
                          onFocus: onZoomFocus,
                          onKeydown: _withKeys(onZoomEnter, ["enter"]),
                          onChange: applyZoomInput
                        }, null, 40, _hoisted_12)
                      ]),
                      _cache[9] || (_cache[9] = _createElementVNode("span", null, "%", -1))
                    ]),
                    _createVNode(_component_KBtn, {
                      model: "link",
                      size: "md",
                      icon: "zoom-in",
                      "custom-color": "static-light",
                      "aria-label": __props.text.zoomIn,
                      onClick: zoomIn
                    }, null, 8, ["aria-label"])
                  ]),
                  _: 1
                })
              ], 2)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_13, [
          _createVNode(_component_KBtn, {
            model: "secondary",
            error: "",
            size: "md",
            padding: "12px",
            label: __props.text.reset,
            class: "k-cropper__action",
            onClick: fitImageAndSelection
          }, null, 8, ["label"]),
          _createVNode(_component_KBtn, {
            model: "primary",
            size: "md",
            padding: "12px",
            label: __props.text.crop,
            class: "k-cropper__action",
            onClick: crop
          }, null, 8, ["label"])
        ])
      ], 512)
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})